import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Users, TrendingUp, Globe, Award, Zap, Shield,
  HeadphonesIcon, Code, Building, Target, Rocket,
  DollarSign, BookOpen, Handshake, CheckCircle, Loader2, Calendar
} from 'lucide-react';
import { toast } from '../components/ui/use-toast';
import { LandingHeader } from '../components/layout/landing-header';

// Turnstile site key
const TURNSTILE_SITE_KEY = '0x4AAAAAACMQBn_yd_5UdWBH';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}

const PartnersPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    partnerType: '',
    message: ''
  });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [calUrl, setCalUrl] = useState<string | null>(null);

  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Load Turnstile script
  useEffect(() => {
    // Check if script already loaded
    if (document.querySelector('script[src*="turnstile"]')) {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(null),
          'expired-callback': () => setTurnstileToken(null),
          theme: 'light',
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (turnstileRef.current && window.turnstile) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(null),
          'expired-callback': () => setTurnstileToken(null),
          theme: 'light',
        });
      }
    };

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast({
        title: "Verification required",
        description: "Please complete the security check before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to CRM via /api/leads
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          website: formData.website,
          message: `[Partner Application - ${formData.partnerType}]\n\n${formData.message}`,
          turnstileToken,
        }),
      });

      const result = await response.json();

      if (result.ok) {
        setSubmitSuccess(true);
        setCalUrl(result.calUrl);
        setFormData({ name: '', email: '', company: '', website: '', partnerType: '', message: '' });

        // Reset Turnstile
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken(null);
        }
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to submit application. Please try again.",
          variant: "destructive",
        });
        // Reset Turnstile on error
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken(null);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Network error. Please check your connection and try again.",
        variant: "destructive",
      });
      // Reset Turnstile on error
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnerBenefits = [
    {
      icon: DollarSign,
      title: 'Revenue Sharing',
      description: 'Earn a recurring commission on all referred customers',
    },
    {
      icon: HeadphonesIcon,
      title: 'Dedicated Support',
      description: 'Get priority access to our partner success team and technical resources',
    },
    {
      icon: BookOpen,
      title: 'Training & Certification',
      description: 'Access exclusive training materials and become a certified PageMind expert',
    },
    {
      icon: Code,
      title: 'API & Integration',
      description: 'Early access to new features and APIs for deeper integration',
    },
    {
      icon: Users,
      title: 'Co-Marketing',
      description: 'Joint marketing opportunities, case studies, and event participation',
    },
    {
      icon: Award,
      title: 'Partner Portal',
      description: 'Exclusive access to partner resources, leads, and performance tracking',
    },
  ];

  const partnerTypes = [
    {
      icon: Building,
      title: 'Agency Partners',
      description: 'Digital agencies and consultancies',
      benefits: [
        'White-label options available',
        'Volume pricing for clients',
        'Co-branded marketing materials',
        'Client onboarding support',
      ],
    },
    {
      icon: Code,
      title: 'Technology Partners',
      description: 'SaaS platforms and tech companies',
      benefits: [
        'API integration support',
        'Joint product roadmap input',
        'Technical documentation',
        'Developer resources',
      ],
    },
    {
      icon: Target,
      title: 'Reseller Partners',
      description: 'Solution providers and resellers',
      benefits: [
        'Competitive margins',
        'Sales enablement tools',
        'Deal registration program',
        'Lead sharing opportunities',
      ],
    },
    {
      icon: Handshake,
      title: 'Referral Partners',
      description: 'Consultants and influencers',
      benefits: [
        'Simple referral process',
        'Transparent commission tracking',
        'Marketing support',
        'No minimum commitments',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader currentPage="partners" />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              <Rocket className="h-4 w-4" />
              <span>Partner Program Now Open</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Grow Your Business with PageMind
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Join our partner ecosystem and help businesses transform their customer engagement with AI-powered conversations
            </p>
            <div className="flex justify-center space-x-4">
              <Button size="lg" onClick={() => document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' })}>
                Become a Partner
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/contact')}>
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Partner with PageMind?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              PageMind is revolutionizing how businesses interact with their website visitors. As a partner, you'll be at the forefront of the AI revolution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <TrendingUp className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Growing Market</h3>
              <p className="text-gray-600">
                The conversational AI market is expected to reach $32B by 2030. Get in early with a proven solution.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Globe className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Global Reach</h3>
              <p className="text-gray-600">
                PageMind supports 50+ languages and serves customers worldwide. Expand your market instantly.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Zap className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Easy to Sell</h3>
              <p className="text-gray-600">
                With instant ROI and visible results, PageMind practically sells itself. Average sales cycle under 2 weeks.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Shield className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Enterprise Ready</h3>
              <p className="text-gray-600">
                GDPR compliant, SOC2 certified, and built for scale. Confidently serve enterprise clients.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Award className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Industry Leader</h3>
              <p className="text-gray-600">
                Partner with the most advanced AI chat solution in the market. Stay ahead of competition.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Users className="h-10 w-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Community</h3>
              <p className="text-gray-600">
                Join a thriving community of partners. Share knowledge, get support, and grow together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Partner Benefits
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to succeed as a PageMind partner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnerBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex">
                  <div className="flex-shrink-0">
                    <Icon className="h-6 w-6 text-purple-600 mt-1" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Partner Programs
            </h2>
            <p className="text-xl text-gray-600">
              Choose the partnership model that fits your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnerTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-4">
                    <Icon className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <h3 className="text-xl font-semibold">{type.title}</h3>
                      <p className="text-sm text-gray-600">{type.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {type.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Application Form */}
      <section id="partner-form" className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Apply to Become a Partner
              </h2>
              <p className="text-gray-600">
                Fill out the form below and we'll get back to you within 48 hours
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Business Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input
                    id="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Acme Inc."
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Company Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="partnerType">Partnership Type *</Label>
                <select
                  id="partnerType"
                  required
                  value={formData.partnerType}
                  onChange={(e) => setFormData({ ...formData, partnerType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select partnership type</option>
                  <option value="agency">Agency Partner</option>
                  <option value="technology">Technology Partner</option>
                  <option value="reseller">Reseller Partner</option>
                  <option value="referral">Referral Partner</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="message">Tell us about your business and partnership goals *</Label>
                <Textarea
                  id="message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your business, target market, and how you plan to promote PageMind..."
                  rows={6}
                />
              </div>

              {/* Turnstile Widget */}
              <div className="flex justify-center py-2">
                <div ref={turnstileRef} />
              </div>

              <div className="flex justify-center">
                <Button
                  type="submit"
                  size="lg"
                  className="px-8"
                  disabled={isSubmitting || !turnstileToken}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </div>
            </form>

            {/* Success Message */}
            {submitSuccess && (
              <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Application Submitted!
                </h3>
                <p className="text-gray-600 mb-4">
                  We'll review your partner application and get back to you within 48 hours.
                </p>
                {calUrl && (
                  <a
                    href={calUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Book a Call
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Partnership Journey?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of partners already succeeding with PageMind
          </p>
          <div className="flex justify-center space-x-4">
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Apply Now
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="bg-transparent text-white border-white hover:bg-white hover:text-purple-600"
              onClick={() => navigate('/contact')}
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;